import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Img from 'gatsby-image/withIEPolyfill';

import { GatsbySanityImageFluid } from '../types';
import * as styles from './ProductCard.module.scss';
import { convertRToSuperscript } from '../utils/utils';
import { PRODUCT_TYPE_COLOR_TO_BARS_IMAGE } from '../constants';
import BlexiaSvg from '../assets/images/blexia.inline.svg';

interface ProductCardProps {
  product: {
    name: string;
    logoImage?: {
      asset?: {
        fluid: GatsbySanityImageFluid;
      };
    };
    image?: {
      asset?: {
        fluid: GatsbySanityImageFluid;
      };
    };

    type: {
      brand: 'ascenza' | 'blexia' | null;
      color: string;
      name: string;
    };
    activeSubstanceInfos?: Array<{
      activeSubstance: {
        name: string;
      };
    }>;
    problemLabel?: string;
    problemDescription?: string;
    cropDescription?: string;
  };
}

const ProductCard = ({ product }: ProductCardProps): React.ReactElement => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  if (!Object.keys(PRODUCT_TYPE_COLOR_TO_BARS_IMAGE).includes(product.type.color)) {
    throw new Error('Got unknown product.type.color: ' + product.type.color);
  }
  const productTypeColor = product.type.color as keyof typeof PRODUCT_TYPE_COLOR_TO_BARS_IMAGE;
  const barsImage = PRODUCT_TYPE_COLOR_TO_BARS_IMAGE[productTypeColor];

  return (
    <div
      className={styles.card + (product.type.brand === 'blexia' ? ' ' + styles.blexiaBrand : '')}
      style={isHovered ? { borderColor: productTypeColor } : undefined}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
    >
      {product.type.brand === 'blexia' ? (
        <div className={styles.blexiaBar}>
          {/* <img src={BlexiaSvg} alt="" /> */}
          <BlexiaSvg className={styles.blexiaLogo} />
        </div>
      ) : (
        <div className={styles.colorBarWrapper}>
          <img src={barsImage} alt=""></img>
        </div>
      )}
      <div className={styles.cardContent}>
        {!!product.logoImage && product.logoImage.asset && product.logoImage.asset.fluid ? (
          <div className={styles.imageTitleContainer}>
            <Img
              className={styles.imageTitle}
              loading="eager"
              fadeIn={false}
              fluid={product.logoImage.asset.fluid}
              alt={product.name}
              objectFit="contain"
              objectPosition="0 0"
            />
          </div>
        ) : (
          <h4 className={styles.title} title={product.name}>
            {convertRToSuperscript(product.name)}
          </h4>
        )}
        <div className={styles.contentRow}>
          {!!product.type && (
            <div className={styles.infoBlock}>
              <span className={styles.label} title={t('products_page.card.type_label', 'Type')}>
                {t('products_page.card.type_label', 'Type')}
              </span>
              <span className={styles.info} title={product.type.name}>
                {product.type.name}
              </span>
            </div>
          )}

          {!!product.activeSubstanceInfos && product.activeSubstanceInfos.length > 0 && (
            <div className={styles.infoBlock}>
              <span
                className={styles.label}
                title={t('products_page.card.active_substances_label', 'Active Substances')}
              >
                {t('products_page.card.active_substances_label', 'Active Substances')}
              </span>
              <span
                className={styles.info}
                title={product.activeSubstanceInfos
                  .map(info => info.activeSubstance.name)
                  .join(', ')}
              >
                {product.activeSubstanceInfos.map(info => info.activeSubstance.name).join(', ')}
              </span>
            </div>
          )}
        </div>

        {!!product.cropDescription && (
          <div className={styles.contentRow}>
            <div className={styles.infoBlock}>
              <span className={styles.label} title={t('products_page.card.crops_label', 'Crops')}>
                {t('products_page.card.crops_label', 'Crops')}
              </span>
              <span className={styles.info} title={product.cropDescription}>
                {product.cropDescription}
              </span>
            </div>
          </div>
        )}

        {!!product.problemLabel && !!product.problemDescription && (
          <div className={styles.contentRow}>
            <div className={styles.infoBlock}>
              <span className={styles.label} title={product.problemLabel}>
                {product.problemLabel}
              </span>
              <span className={styles.info} title={product.problemDescription}>
                {product.problemDescription}
              </span>
            </div>
          </div>
        )}

        {/* MOCK CONTENT ROWS */}

        {!product.activeSubstanceInfos ||
          (product.activeSubstanceInfos.length === 0 && (
            <div
              className={
                styles.contentRow + ' ' + styles.hidden + ' ' + styles.activeSubstancesMock
              }
            >
              <div className={styles.infoBlock}>
                <span className={styles.label}>X</span>
                <span className={styles.info}>X</span>
              </div>
            </div>
          ))}

        {!product.cropDescription && (
          <div className={styles.contentRow + ' ' + styles.hidden}>
            <div className={styles.infoBlock}>
              <span className={styles.label}>X</span>
              <span className={styles.info}>X</span>
            </div>
          </div>
        )}

        {(!product.problemLabel || !product.problemDescription) && (
          <div className={styles.contentRow + ' ' + styles.hidden}>
            <div className={styles.infoBlock}>
              <span className={styles.label}>X</span>
              <span className={styles.info}>X</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
